import {
  formatSessionTimeoutLabel,
  resolveSessionTimeout,
} from './session-timeout';

export const CHAT_FONT_SIZE_VALUES = ['sm', 'md', 'lg'] as const;

export type ChatFontSize = (typeof CHAT_FONT_SIZE_VALUES)[number];

export const CHAT_FONT_SCALE_FACTORS: Record<ChatFontSize, number> = {
  sm: 1,
  md: 1.15,
  lg: 1.3,
};

export const WIDGET_LAUNCHER_STYLE_VALUES = [
  'icon_only',
  'icon_with_name',
] as const;

export type WidgetLauncherStyle = (typeof WIDGET_LAUNCHER_STYLE_VALUES)[number];

// Where the quick start buttons appear in the widget.
// "within_chat": after the intro message inside the opened chat (default)
// "on_hover": floating above the FAB launcher when hovered on desktop
export const QUICK_START_PLACEMENT_VALUES = [
  'within_chat',
  'on_hover',
] as const;

export type QuickStartPlacement = (typeof QUICK_START_PLACEMENT_VALUES)[number];

// Whether quick start buttons appear on every new chat or only until the visitor's first message.
export const QUICK_START_FREQUENCY_VALUES = [
  'every_new_chat',
  'until_first_message',
] as const;

export type QuickStartFrequency = (typeof QUICK_START_FREQUENCY_VALUES)[number];

export const LAUNCHER_TEASER_DEFAULT_MESSAGE =
  'Anything we can help with?' as const;

export const LAUNCHER_TEASER_STYLE_VALUES = ['pulse', 'message'] as const;

export type LauncherTeaserStyle = (typeof LAUNCHER_TEASER_STYLE_VALUES)[number];

export const CHAT_BUTTON_ACCENT_VALUES = [
  'primary_only',
  'accent_icon',
  'accent_outline',
] as const;

export type ChatButtonAccent = (typeof CHAT_BUTTON_ACCENT_VALUES)[number];

export const CHAT_HEIGHT_VALUES = ['compact', 'full_height'] as const;

export type ChatHeight = (typeof CHAT_HEIGHT_VALUES)[number];

export type AnalyticsChatHeightValue = 'full' | 'compact';

/** Display order matches SEM-6930 mock (Full Height first). */
export const CHAT_HEIGHT_DISPLAY_ORDER: ChatHeight[] = [
  'full_height',
  'compact',
];

export const CHAT_HEIGHT_OPTION_DETAILS: Record<
  ChatHeight,
  { label: string; description: string }
> = {
  full_height: {
    label: 'Full Height',
    description: 'The chat fills the available height of the screen.',
  },
  compact: {
    label: 'Compact',
    description: 'A shorter view that leaves more of your page visible.',
  },
};

export function toAnalyticsChatHeightValue(
  height: ChatHeight
): AnalyticsChatHeightValue {
  return height === 'compact' ? 'compact' : 'full';
}

export {
  SESSION_TIMEOUT_VALUES,
  SESSION_TIMEOUT_DEFAULT,
  SESSION_TIMEOUT_OPTION_LABELS,
  type SessionTimeout,
  type SessionTimeoutPreset,
  type SessionTimeoutUnit,
} from './session-timeout';

export function getSessionTimeoutHelperText(value: string): string {
  if (value === 'never') {
    return 'Visitors keep the same conversation until they clear their browser data. This is how the chat has worked until now.';
  }
  const durationLabel =
    formatSessionTimeoutLabel(value) ?? formatSessionTimeoutLabel('30d');
  return `If a visitor comes back after ${durationLabel} without chatting, they start a new conversation as a new visitor.`;
}

export function getQuickStartFrequencyUntilFirstMessageCaption(
  sessionTimeout?: string | null
): string {
  const token = resolveSessionTimeout(sessionTimeout);
  const durationLabel =
    token !== 'never' ? formatSessionTimeoutLabel(token) : null;
  const returnLine = durationLabel
    ? `They come back after the visitor has been away for ${durationLabel}.`
    : 'They come back only if the visitor clears their browser data.';
  return `Once a visitor sends a message, the buttons stay hidden on other pages and tabs. ${returnLine}`;
}

export const MAX_LAUNCHER_TEASER_PAGE_OVERRIDES = 10;
